/**
 * Phase 20.3 — ShuffleCard
 * Renders a single repost card for the RGM Shuffle feed.
 * Supports both `kind=track` (artist) and `kind=beat` (producer) shapes.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { usePlayer } from "@/components/player/PlayerProvider";
import { getProfilePhotoUrl, optimizeImage } from "@/lib/mediaUrls";
import GiftPicker from "@/components/GiftPicker";
import AddBeatToPlaylistDialog from "@/components/AddBeatToPlaylistDialog";

const ShuffleCard = ({ item, currentUserId }) => {
  const navigate = useNavigate();
  const player = usePlayer();
  const [showAddToPlaylist, setShowAddToPlaylist] = useState(false);

  const isTrack = item.kind === "track";
  const subject = isTrack ? item.track : item.beat;
  const subjectId = isTrack ? subject?.submission_id : subject?.beat_id;
  const detailHref = isTrack ? `/track/${subjectId}` : `/beats/${subjectId}`;

  const trackId = isTrack ? subjectId : `beat_${subjectId}`;
  const isCurrent = player?.track?.trackId === trackId;
  const playing = !!isCurrent && player?.isPlaying;

  const handlePlay = (e) => {
    e?.stopPropagation?.();
    if (!subject?.audio_url) {
      toast.error("No audio source available");
      return;
    }
    if (isCurrent) {
      // Already current — toggle play/pause but also open full-screen
      // so the experience matches "tap a currently playing shuffle item".
      player.toggle();
      navigate("/shuffle/radio-player");
      return;
    }
    const basePayload = isTrack
      ? {
          submission_id: subjectId,
          trackId: subjectId,
          title: subject.title || subject.track_title,
          artist_name: item.creator?.name || "Artist",
          artist_id: item.creator?.user_id,
          cover_url: subject.cover_url,
          audio_url: subject.audio_url,
          kind: "track",
        }
      : {
          beat_id: subjectId,
          trackId,
          title: subject.title,
          artist_name: item.creator?.name || "Producer",
          artist_id: item.creator?.user_id,
          cover_url: subject.cover_url,
          audio_url: subject.audio_url,
          kind: "beat",
        };
    // Tag with shuffle station context so the analytics tracker fires
    // and the full-screen player recognises this as a radio session.
    const payload = {
      ...basePayload,
      _station: "community",
      source: "repost",
      queue_id: item.repost_id || null,
    };
    player.play(payload, { sourceLabel: "🔀 RGM Shuffle · Community" });
    if (item.repost_id) {
      api.post(`/shuffle/${item.repost_id}/play`).catch(() => {});
    }
    // Phase 20.6.2 — open the dedicated full-screen radio player
    navigate("/shuffle/radio-player");
  };

  const handleClick = () => {
    if (item.repost_id) {
      api.post(`/shuffle/${item.repost_id}/click`).catch(() => {});
    }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}${detailHref}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch { toast.error("Could not copy link"); }
  };

  return (
    <>
    <Card
      className="overflow-hidden bg-white/[0.04] border-white/10 hover:border-purple-400/40 transition group"
      data-testid={`shuffle-card-${item.repost_id || subjectId}`}
    >
      {/* Reposter badge */}
      <div className="px-3 pt-3 flex items-center gap-2 text-[11px] text-gray-400">
        <img
          src={getProfilePhotoUrl({ profile_photo: item.reposter?.profile_photo, picture: item.reposter?.profile_photo })}
          alt=""
          className="w-5 h-5 rounded-full border border-purple-400/20 object-cover"
        />
        <span>
          <span className="text-purple-300">🔁</span>{" "}
          <Link to={`/artist/${item.reposter?.user_id}`}
                className="hover:underline text-purple-300"
                onClick={handleClick}>
            {item.reposter?.name || "Someone"}
          </Link>{" "}
          reposted a {isTrack ? "track" : "beat"}
        </span>
      </div>

      <Link to={detailHref} onClick={handleClick} className="block">
        <div className="aspect-square w-full bg-black/50 relative overflow-hidden mt-2">
          {subject?.cover_url && (
            <img src={optimizeImage(subject.cover_url, 'card')} alt={subject.title || subject.track_title}
                 className="w-full h-full object-cover" loading="lazy" />
          )}
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); handlePlay(e); }}
            className="absolute inset-0 flex items-center justify-center text-5xl text-white opacity-0 group-hover:opacity-100 transition bg-black/40"
            data-testid={`shuffle-play-${item.repost_id || subjectId}`}
            aria-label={playing ? "Pause" : "Play"}
          >
            {playing ? "❚❚" : "▶"}
          </button>
          <span
            className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-black uppercase
              ${isTrack
                ? "bg-purple-500/30 border border-purple-300 text-purple-100"
                : "bg-pink-500/30 border border-pink-300 text-pink-100"}`}
            data-testid={`shuffle-kind-${item.repost_id || subjectId}`}
          >
            {isTrack ? "🎤 Track" : "🎹 Beat"}
          </span>
          {!isTrack && (subject?.gifts_received || 0) > 0 && (
            <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-black/80 text-amber-200 text-[10px] font-bold border border-amber-400/30">
              🎁 {subject.gifts_received}
            </span>
          )}
        </div>
      </Link>

      <div className="p-3 space-y-2">
        <div className="flex items-start gap-2">
          <img
            src={getProfilePhotoUrl({ profile_photo: item.creator?.profile_photo, picture: item.creator?.profile_photo })}
            alt=""
            className="w-7 h-7 rounded-full border border-white/15 flex-shrink-0 object-cover"
          />
          <div className="min-w-0 flex-1">
            <Link to={`/artist/${item.creator?.user_id}`} className="text-[11px] text-gray-400 hover:text-purple-300 truncate block">
              {item.creator?.name || (isTrack ? "Artist" : "Producer")}
            </Link>
            <Link to={detailHref} className="text-sm font-bold text-white truncate hover:text-pink-300 block"
                  data-testid={`shuffle-title-${item.repost_id || subjectId}`}>
              {subject?.title || subject?.track_title || "Untitled"}
            </Link>
          </div>
        </div>

        {/* Action row */}
        <div className="flex items-center gap-1.5 pt-1">
          <Button
            onClick={handlePlay}
            className="flex-1 h-7 text-[11px] bg-purple-600/30 hover:bg-purple-600/50 border border-purple-400/40 text-white"
            data-testid={`shuffle-action-play-${item.repost_id || subjectId}`}
          >
            {playing ? "❚❚ Pause" : "▶ Play"}
          </Button>
          {!isTrack && currentUserId && item.creator?.user_id !== currentUserId && (
            <GiftPicker
              targetType="beat"
              targetId={subjectId}
              recipientId={item.creator?.user_id}
              currentUserId={currentUserId}
              compact
              className="shrink-0"
            />
          )}
          <Link to={detailHref + (isTrack ? "#comments" : "")}
                onClick={handleClick}
                className="px-2 h-7 text-[11px] rounded-md bg-white/5 border border-white/10 text-gray-300 hover:border-purple-400/40 hover:text-white inline-flex items-center"
                data-testid={`shuffle-comment-${item.repost_id || subjectId}`}
                title="Comment">
            💬
          </Link>
          <button
            onClick={handleShare}
            className="px-2 h-7 text-[11px] rounded-md bg-white/5 border border-white/10 text-gray-300 hover:border-purple-400/40 hover:text-white"
            data-testid={`shuffle-share-${item.repost_id || subjectId}`}
            title="Share"
          >↗</button>
          {!isTrack && currentUserId && (
            <button
              onClick={() => setShowAddToPlaylist(true)}
              className="px-2 h-7 text-[11px] rounded-md bg-white/5 border border-white/10 text-gray-300 hover:border-purple-400/40 hover:text-white"
              data-testid={`shuffle-add-to-playlist-${item.repost_id || subjectId}`}
              title="Add to playlist"
            >🎼+</button>
          )}
        </div>
      </div>
    </Card>
    {!isTrack && (
      <AddBeatToPlaylistDialog
        open={showAddToPlaylist}
        onClose={() => setShowAddToPlaylist(false)}
        beat={{ beat_id: subjectId, title: subject?.title }}
      />
    )}
    </>
  );
};

export default ShuffleCard;
